import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, apiPost, setUnauthorizedHandler } from '@/lib/api';
import type { User } from '@/types';
import { companionBriefKey } from '@/lib/companion';
import { releasePush } from '@/lib/push';

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string, remember: boolean, turnstileToken?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  /* session 失效時（任何 API 回 401）立刻清掉使用者，畫面自動退回登入頁。 */
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
  }, []);

  /* 開機先問一次 /auth/me：帶著有效 cookie 重整不該被踢回登入頁，
     這是「30 天內保持登入」能成立的關鍵。 */
  useEffect(() => {
    let alive = true;
    api<User>('/api/v1/auth/me')
      .then((u) => alive && setUser(u))
      .catch(() => alive && setUser(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const login = useCallback(
    async (username: string, password: string, remember: boolean, turnstileToken = '') => {
      const u = await apiPost<User>('/api/v1/auth/login', {
        username, password, remember, turnstile_token: turnstileToken,
      });
      try { sessionStorage.removeItem(companionBriefKey(u.username)); }
      catch { /* 不允許分頁儲存時，仍可登入。 */ }
      setUser(u);
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      // 先撤銷這台瀏覽器的推播綁定（需要登入狀態），下一位使用者才不會收到前一位的通知
      await releasePush();
      await apiPost('/api/v1/auth/logout');
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth 必須在 AuthProvider 內使用');
  return v;
}
